import { ReadOnlyRef, useMemo, useRef, useState } from "@lukekaalim/act";
import { SurfaceID, MiniID, MiniTemplateID, ToolState, PhysicsDiceID } from "../defs";
import { Observable, Subject } from "rxjs";
import { subscribeEvent, useJeffect, useLogger, Vector } from "@kosmod/kit";
import { ToolID } from "../defs/tools/define";
import { DiceExpression } from "@kosmod/andy";
import { useDisposable } from "@kosmod/graphics-3d";
import { ToolBeltID } from "./useToolController";

export type TheatreSelection =
  | { type: 'none' }
  | { type: 'mini', miniId: MiniID }
  | { type: 'physics-dice', physicsDieId: PhysicsDiceID[] }
  // | { type: 'terrain', floors: { surface: SurfaceID, vectors: Vector<2>[] }, walls: unknown[] }

export type TheatreHoverTarget =
  | { type: 'none' }
  | { type: 'mini', miniId: MiniID }

export type TheatreDragSubject =
  | { type: 'mini-template', miniTemplateID: MiniTemplateID }
  | { type: 'tool', toolId: ToolID, toolState: ToolState }
  | { type: 'die', diceExpression: DiceExpression }

export type TheatreDragSource =
  | { type: 'toolbelt', toolbeltId: ToolBeltID }
  | { type: 'thin-air' }
  
export type TheatreDrag = {
  subject: TheatreDragSubject,
  source: TheatreDragSource,
}

export type TheatreSelectionController = {
  target: TheatreSelection,

  hover: TheatreHoverTarget,

  drag: null | TheatreDrag,

  startDrag(subject: TheatreDragSubject, source?: TheatreDragSource | null): void,
  finishDrag(): void,

  select(selection: TheatreSelection): void,
  setHoverTarget(hoverTarget: TheatreHoverTarget): void,

  onSelectionChange: Observable<TheatreSelection>,
  onHoverChange: Observable<TheatreHoverTarget>,


  emptyDragImage: HTMLElement,
}

const isSelectionTargetEqual = (left: TheatreSelection, right: TheatreSelection) => {
  if (left.type !== right.type)
    return false;

  switch (left.type) {
    case 'mini':
      return left.miniId === (right as Extract<TheatreSelection, { type: 'mini' }>).miniId;
    case 'none':
      return true;
    default:
      return false;
  }
}

export const useTheatreSelectionController = (containerRef: ReadOnlyRef<HTMLElement | null>): TheatreSelectionController => {
  const [target, setTarget] = useState<TheatreSelection>({ type: 'none' });
  const [hover, setHover] = useState<TheatreHoverTarget>({ type: 'none' });

  const [drag, setDrag] = useState<null | TheatreDrag>(null);

  const onSelectionChange = useRef(() => new Subject<TheatreSelection>()).get()
  const onHoverChange = useRef(() => new Subject<TheatreHoverTarget>()).get()

  const logger = useLogger('TheatreSelectionController');

  const select = useMemo(() => (selection: TheatreSelection = { type: 'none' }) => {
    if (isSelectionTargetEqual(target, selection))
      return;
    setTarget(selection);
    onSelectionChange.next(selection);
  }, [target])

  const setHoverTarget = useMemo(() => (target: TheatreHoverTarget = { type: 'none' }) => {
    setHover(target);
    onHoverChange.next(target);
  }, [])

  const finishDrag = useMemo(() => {
    return () => {
      setDrag(null)
    }
  }, [])
  const startDrag = useMemo(() => {
    return (subject: TheatreDragSubject, source: TheatreDragSource = { type: 'thin-air' }) => {
      setDrag({ subject, source })
    }
  }, [])

  useJeffect(j => {
    if (!drag)
      return;

    const container = containerRef.get()
    if (!container)
      return;

    j.add(subscribeEvent(container, 'dragend', event => {
      logger.log(`Fallback dragend detected`)
      if (event.defaultPrevented)
        return;

      finishDrag();
    }))
  }, [drag])

  const { emptyDragImage } = useDisposable(() => {
    const emptyDragImage = document.createElement('div');
    document.body.append(emptyDragImage);

    emptyDragImage.style.width = '1px';
    emptyDragImage.style.height = '1px';
    emptyDragImage.style.display = 'block';
    emptyDragImage.style.background = '#ffffff'

    return { emptyDragImage, dispose() {
      emptyDragImage.remove()
    } }
  }, [])

  return useMemo(() => ({
    select,
    target,
    setHoverTarget,
    hover,
    onSelectionChange,
    onHoverChange,

    finishDrag,
    startDrag,
    
    drag,
    emptyDragImage,
  }), [target, hover, drag]);
};
